import { Component, OnInit, OnDestroy } from '@angular/core';
import { SocketService } from './core/services/socket.service';
import { NotificationService } from './core/services/notification.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'velzon';
  private socketSubscriptions: Subscription[] = [];

  constructor(
    private socketService: SocketService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeSocketConnection();
  }

  private initializeSocketConnection(): void {
    const token = JSON.parse(localStorage.getItem('currentUser') || 'null')?.token?.token;
    
    if (!token) {
      console.log('No token available, skipping socket connection');
      return;
    }

    this.socketService.connect();
    this.setupGlobalSocketListeners();
  }

  private setupGlobalSocketListeners(): void {
    
    // Listen for new incidents
    const incidentCreatedSub = this.socketService.onIncidentCreated$.subscribe((data) => {
      if (data && data.incident) {
        console.log('New incident received via socket:', data.incident);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'error',
          title: 'New Incident Reported!',
          html: `<strong>${data.incident.incidentNo}</strong> at ${data.incident.location}`,
          confirmButtonColor: '#B71C1C',
          timer: 8000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for vehicle assignments
    const vehicleAssignedSub = this.socketService.onVehicleAssigned$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Vehicle assigned via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'success',
          title: 'Vehicle Assigned!',
          text: `Vehicle has been assigned to Incident ${data.incident.incidentNo}`,
          confirmButtonColor: '#405189',
          timer: 50000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for crew assignments
    const crewAssignedSub = this.socketService.onCrewAssigned$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Crew assigned via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'success',
          title: 'Crew Assigned!',
          text: `Crew has been assigned to Incident ${data.incident.incidentNo}`,
          confirmButtonColor: '#405189',
          timer: 50000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for incident updates
    const incidentUpdatedSub = this.socketService.onIncidentUpdated$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Incident updated via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'info',
          title: 'Incident Updated!',
          text: `Incident ${data.incident.incidentNo} has been updated`,
          confirmButtonColor: '#405189',
          timer: 5000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for additional vehicle requests
    const additionalVehicleRequestedSub = this.socketService.onAdditionalVehicleRequested$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Additional vehicle requested via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'warning',
          title: 'Additional Vehicle Requested!',
          text: `Additional vehicle requested for Incident ${data.incident.incidentNo}`,
          confirmButtonColor: '#405189',
          timer: 5000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for additional vehicle assignments
    const additionalVehicleAssignedSub = this.socketService.onAdditionalVehicleAssigned$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Additional vehicle assigned via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'success',
          title: 'Additional Vehicle Assigned!',
          text: `Additional vehicle assigned to Incident ${data.incident.incidentNo}`,
          confirmButtonColor: '#405189',
          timer: 5000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    // Listen for participant status updates
    const participantStatusUpdatedSub = this.socketService.onParticipantStatusUpdated$.subscribe((data) => {
      if (data && data.incident) {
        console.log('Participant status updated via socket:', data);
        
        this.notificationService.playAlarmSound();
        
        Swal.fire({
          icon: 'info',
          title: 'Status Updated!',
          text: `Participant status updated for Incident ${data.incident.incidentNo}`,
          confirmButtonColor: '#405189',
          timer: 5000,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timerProgressBar: true
        });
      }
    });

    this.socketSubscriptions.push(
      incidentCreatedSub,
      vehicleAssignedSub,
      crewAssignedSub,
      incidentUpdatedSub,
      additionalVehicleRequestedSub,
      additionalVehicleAssignedSub,
      participantStatusUpdatedSub
    );
  }

  ngOnDestroy(): void {
    this.socketSubscriptions.forEach(sub => sub.unsubscribe());
    this.socketService.disconnect();
  }
}
